import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FilePdf, FileDoc, FileJpg, Download } from '@phosphor-icons/react';
import { formatDate, formatFileSize } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  fetchDocuments,
  uploadDocumentFile,
  getApiErrorMessage,
} from '@/lib/api';

const DOCUMENTS_PREFIX = 'documents';

function fileTypeFromName(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'jpg';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  return 'pdf';
}

export default function DocumentsPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDocuments(DOCUMENTS_PREFIX);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return FilePdf;
      case 'doc':
        return FileDoc;
      case 'jpg':
        return FileJpg;
      default:
        return FilePdf;
    }
  };

  const handleUpload = async () => {
    const input = fileInputRef.current;
    const file = input?.files?.[0];
    if (!file) {
      toast.error('Choose a file to upload');
      return;
    }
    setUploading(true);
    try {
      await uploadDocumentFile(file, DOCUMENTS_PREFIX);
      toast.success('Document uploaded');
      setIsDialogOpen(false);
      if (input) input.value = '';
      await loadDocuments();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc) => {
    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">
            Documents
          </h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage medical documents and files
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-document-button">Upload Document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Files are stored securely. Upload a PDF, image, or other file.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="doc-file">File</Label>
                <input
                  id="doc-file"
                  ref={fileInputRef}
                  type="file"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium"
                />
              </div>
              <Button
                className="w-full"
                disabled={uploading}
                onClick={handleUpload}
              >
                {uploading ? 'Uploading…' : 'Upload Document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading documents…</p>
      ) : documents.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No documents yet. Use Upload Document to add files.
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documents.map((doc) => {
            const type = fileTypeFromName(doc.displayName || doc.objectName);
            const Icon = getFileIcon(type);
            return (
              <Card
                key={doc.objectName}
                className="p-4 hover-lift"
                data-testid={`document-card-${doc.objectName}`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-primary/10">
                    <Icon className="h-6 w-6 text-primary" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" title={doc.displayName}>
                      {doc.displayName}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatFileSize(doc.size)} •{' '}
                      {doc.updated
                        ? formatDate(doc.updated, 'short')
                        : '—'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label="Download"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
